import { GameScene } from '../GameScene';
import { mapIterateCells } from './mapTools';
import { blockIsBasic } from './blockTools';
import { GoalId } from '../defs/goal';
import { BlockId, blockPropsMap } from '../defs/block';
import { track } from 'src/lib/analytics/utils';

/**
 * Convert an object to a readable string, sligthly shorter than JSON.stringify
 * @param obj
 * @returns
 */
function objToStr(obj: Record<string, number>) {
  const items = [];
  for (const key in obj) {
    items.push(`${key}:${obj[key]}`);
  }
  return items.join(' ');
}

/**
 * Log a sanity-check event with some common properties
 * @param game
 * @param event
 * @param props
 * @returns
 */
function logEvent(
  game: GameScene,
  event: 'SanityCheckPuzzleDefeat',
  props: { message: string } & Record<string, number | string | boolean>,
) {
  if (!game.mapDef) return;
  track(event, {
    ...props,
    level: game.level,
    moves: game.moves,
    remainingGoals: objToStr(game.sessionEntity.c.map.goals),
    remainingGoalsCount: game.getRemainingGoals(),
    remainingObstacles: objToStr(getRemainingObstaclesInBoard(game)),
    remainingObstaclesCount: countRemainingObstaclesInBoard(game),
  });
}

/**
 * Check the board and map all remaining obstacles
 * @param game
 * @returns
 */
function getRemainingObstaclesInBoard(game: GameScene) {
  const obstacles: Partial<Record<BlockId, number>> = {};
  mapIterateCells(game, (cell) => {
    if (cell.base) {
      const blockId = cell.base.entity.c.block.blockId;
      if (!blockIsBasic(blockId)) {
        obstacles[blockId] = (obstacles[blockId] ?? 0) + 1;
      }
    }
    if (cell.overlay) {
      const blockId = cell.overlay.entity.c.block.blockId;
      if (!blockIsBasic(blockId)) {
        obstacles[blockId] = (obstacles[blockId] ?? 0) + 1;
      }
    }
  });
  return obstacles;
}

/**
 * Count all remaining obstacles in the board
 * @param game
 * @returns
 */
function countRemainingObstaclesInBoard(game: GameScene) {
  return Object.values(getRemainingObstaclesInBoard(game)).reduce(
    (acc, count) => acc + count,
    0,
  );
}

/**
 * Retrieve obstacles related to a goal
 * @param goalId
 * @returns
 */
function getObstaclesForGoal(goalId: GoalId) {
  const obstacles: BlockId[] = [];

  // Include the goal itself if it is an obstacle
  if (blockPropsMap[goalId]) {
    obstacles.push(goalId);
  }

  for (const key in blockPropsMap) {
    const blockId = key as BlockId;
    if (obstacles.includes(blockId)) continue;
    const props = blockPropsMap[blockId];
    if (
      blockId === goalId ||
      props.sharedGoal === goalId ||
      props.customGoal === goalId
    ) {
      obstacles.push(blockId);
    }
  }
  return obstacles;
}

/**
 * Run some sanity checks on the game state after a defeat
 * @param game The game scene to check
 * @returns true if the defeat is valid
 */
export function puzzleDefeatSanityCheck(game: GameScene) {
  // Only perform sanity check if game is active, so data and goals are up-to-date
  if (!game.active) return false;

  const event = 'SanityCheckPuzzleDefeat';

  // Check if game result is actually a defeat
  if (game.getResult() !== 'lose') {
    logEvent(game, event, {
      message: 'Game result is not lose',
    });
    return false;
  }

  // Check if there are moves remaining
  if (game.moves > 0) {
    logEvent(game, event, {
      message: 'Defeat with moves remaining',
    });
    return false;
  }

  // Check if there are goals remaining
  if (game.getRemainingGoals() === 0) {
    logEvent(game, event, {
      message: 'Defeat with no remaining goals',
    });
    return false;
  }

  // Find out all remaining obstacles in the board
  const obstaclesInBoard = getRemainingObstaclesInBoard(game);

  // Loop through all goals and check if they make sense
  for (const key in game.sessionEntity.c.map.goals) {
    const goalId = key as GoalId;
    const goalCount = game.sessionEntity.c.map.goals[goalId];

    // Goal cleared, skip
    if (!goalCount) continue;

    // At least one count should be positive on defeat
    if (goalCount < 0) {
      logEvent(game, event, {
        message: 'Goal has negative count',
        goalId,
        goalCount: goalCount,
      });
      return false;
    }

    // Find out the obstacle related to the goal
    const obstacles = getObstaclesForGoal(goalId);

    // If not defined, the goal does not have an obstacle related in data
    if (!obstacles.length) {
      logEvent(game, event, {
        message: 'Goal has no related obstacle',
        goalId,
      });
      return false;
    }

    // Check if there are any of these obstacles in the board
    const hasObstacleInBoard = obstacles.some(
      (obstacle) => !!obstaclesInBoard[obstacle],
    );

    // Check if there are any of these obstacles in the spawn list
    const hasObstacleInSpawn = obstacles.some((obstacle) =>
      game.mapDef?.spawns.some((spawn) => spawn === obstacle),
    );

    // Check if there are at least one obstacle in the board or in spawn list for the goal
    if (!hasObstacleInBoard && !hasObstacleInSpawn) {
      logEvent(game, event, {
        message: 'Goal has no related obstacle in the board',
        goalId,
      });
      return false;
    }
  }

  // All checks passed, must be a valid defeat
  return true;
}
